import React, { useState, useRef, useCallback } from 'react';
import { AttributeService } from '../modules/attribute/AttributeService';
import { API_BASE as API_URL, getToken} from '../../../lib/api';

// ─── Valid field types list ───────────────────────────────────────────────────
const VALID_TYPES = [
  'text','textarea','number','decimal','password','boolean',
  'dropdown','multi-select','radio','checkbox',
  'date','time','datetime',
  'file-upload','image-url',
  'lookup','autocomplete','entity-picker',
  'calculated','formula',
  'grid','address',
  'barcode','qr-code','currency','tax-selector','status','workflow',
];

const VALID_SCOPES = ['form','control','solution','global'];

// ─── CSV Template columns & sample rows ──────────────────────────────────────
const TEMPLATE_HEADERS = [
  'id','label','type','industry','scope','placeholder','helpText',
  'required','readOnly','min','max','minLength','maxLength',
  'pattern','patternMessage',
  'options','defaultValue',
  'dbTableName','dbColumnName',
  'unit',
];

const TEMPLATE_SAMPLE_ROWS = [
  ['product_color','Product Color','multi-select','Common','form','','Select colors',
   'false','false','','','','',
   '','',
   'red:Red|blue:Blue|green:Green|black:Black|white:White','',
   '','',''],
  ['product_weight','Product Weight','number','Common','form','e.g. 0.5','Weight in kg',
   'false','false','0','1000','','',
   '','',
   '','',
   '','','kg'],
  ['apparel_gender','Gender','dropdown','Apparel','form','','',
   'false','false','','','','',
   '','',
   'men:Men|women:Women|unisex:Unisex|kids:Kids','',
   '','',''],
  ['expiry_date','Expiry Date','date','Common','form','','Must be a future date',
   'true','false','','','','',
   '','',
   '','',
   '','',''],
  ['hsn_code','HSN Code','text','Common','form','e.g. 6205','Harmonized System Nomenclature',
   'false','false','','','4','8',
   '^[0-9]{4,8}$','Must be 4-8 digit numeric code',
   '','',
   'T_Items','HSN_Code',''],
];

// ─── Parser ───────────────────────────────────────────────────────────────────

function parseOptions(raw) {
  if (!raw || !raw.trim()) return [];
  return raw.split('|').map(pair => {
    const [value, ...rest] = pair.split(':');
    return { value: value.trim(), label: (rest.join(':') || value).trim() };
  }).filter(o => o.value);
}

function parseCSVLine(line) {
  const result = [];
  let current = '';
  let inQuotes = false;
  for (let i = 0; i < line.length; i++) {
    const ch = line[i];
    if (ch === '"') {
      if (inQuotes && line[i+1] === '"') { current += '"'; i++; }
      else inQuotes = !inQuotes;
    } else if (ch === ',' && !inQuotes) {
      result.push(current.trim());
      current = '';
    } else {
      current += ch;
    }
  }
  result.push(current.trim());
  return result;
}

function csvToAttributes(csvText) {
  const lines = csvText.split('\n').map(l => l.replace(/\r$/, '')).filter(l => l.trim());
  if (lines.length < 2) return [];

  const headers = parseCSVLine(lines[0]).map(h => h.toLowerCase().replace(/[^a-z0-9]/g,''));
  const rows = [];

  for (let i = 1; i < lines.length; i++) {
    const line = lines[i].trim();
    if (!line) continue;
    const cols = parseCSVLine(line);
    const row = {};
    headers.forEach((h, idx) => { row[h] = (cols[idx] || '').trim(); });

    // Build attribute object
    const attr = {
      id: row['id'] || '',
      label: row['label'] || '',
      type: row['type'] || 'text',
      industry: row['industry'] || 'Common',
      scope: row['scope'] || 'form',
    };
    if (row['placeholder']) attr.placeholder = row['placeholder'];
    if (row['helptext']) attr.helpText = row['helptext'];
    if (row['unit']) attr.unit = row['unit'];
    if (row['dbtablename']) attr.dbTableName = row['dbtablename'];
    if (row['dbcolumnname']) attr.dbColumnName = row['dbcolumnname'];
    if (row['defaultvalue']) attr.defaultValue = row['defaultvalue'];

    // Validation
    const validation = {};
    if (row['required'] === 'true') validation.required = true;
    if (row['min']) validation.min = Number(row['min']);
    if (row['max']) validation.max = Number(row['max']);
    if (row['minlength']) validation.minLength = Number(row['minlength']);
    if (row['maxlength']) validation.maxLength = Number(row['maxlength']);
    if (row['pattern']) validation.pattern = row['pattern'];
    if (row['patternmessage']) validation.patternMessage = row['patternmessage'];
    if (Object.keys(validation).length > 0) attr.validation = validation;

    if (row['readonly'] === 'true') attr.readOnly = true;

    // Options
    const opts = parseOptions(row['options']);
    if (opts.length > 0) attr.options = opts;

    // Validate
    const errors = [];
    if (!attr.id) errors.push('id is required');
    if (!attr.label) errors.push('label is required');
    if (!VALID_TYPES.includes(attr.type)) errors.push(`invalid type "${attr.type}"`);
    if (!VALID_SCOPES.includes(attr.scope)) errors.push(`invalid scope "${attr.scope}"`);
    if (['dropdown','multi-select','radio','status'].includes(attr.type) && (!attr.options || attr.options.length === 0)) {
      errors.push(`${attr.type} requires options (format: value:Label|value2:Label2)`);
    }

    rows.push({ ...attr, _rowNum: i, _errors: errors, _valid: errors.length === 0 });
  }
  return rows;
}

// ─── Generate CSV bytes ───────────────────────────────────────────────────────

function generateTemplate() {
  const lines = [TEMPLATE_HEADERS.join(',')];
  for (const row of TEMPLATE_SAMPLE_ROWS) {
    lines.push(row.map(c => (c.includes(',') || c.includes('"') ? `"${c.replace(/"/g,'""')}"` : c)).join(','));
  }
  return lines.join('\n');
}

// ─── Spinner ─────────────────────────────────────────────────────────────────
function Spinner({ size = 16, color = '#2563eb' }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" style={{ animation:'spin 0.8s linear infinite', flexShrink:0 }}>
      <style>{`@keyframes spin{from{transform:rotate(0deg)}to{transform:rotate(360deg)}}`}</style>
      <circle cx="12" cy="12" r="9" stroke={color} strokeWidth="2.5" strokeDasharray="40 20" strokeLinecap="round"/>
    </svg>
  );
}

// ─── Main BulkImportTab ───────────────────────────────────────────────────────
export function BulkImportTab({ onImportComplete }) {
  const [csvText, setCsvText] = useState('');
  const [parsed, setParsed] = useState(null);   // array of attribute rows
  const [isDragging, setIsDragging] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null); // { imported, failed, results }
  const [activeInput, setActiveInput] = useState('upload'); // 'upload' | 'paste'
  const fileRef = useRef(null);

  const accent = '#2563eb';
  const borderColor = '#e2e8f0';
  const textPrimary = '#0f172a';
  const textMuted = '#64748b';
  const inp = { width:'100%', padding:'0.42rem 0.65rem', border:`1px solid ${borderColor}`, borderRadius:'6px', fontSize:'0.82rem', outline:'none', background:'#fafafa', color:textPrimary, fontFamily:'inherit' };

  // Download template
  const downloadTemplate = useCallback(() => {
    const csv = generateTemplate();
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'attribute_import_template.csv';
    a.click();
    URL.revokeObjectURL(url);
  }, []);

  // Parse CSV text
  const parseAndPreview = useCallback((text) => {
    setCsvText(text);
    if (!text.trim()) { setParsed(null); setImportResult(null); return; }
    const rows = csvToAttributes(text);
    setParsed(rows);
    setImportResult(null);
  }, []);

  // Handle file upload
  const handleFile = useCallback((file) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = e => {
      const text = e.target.result;
      setActiveInput('paste');
      parseAndPreview(text);
    };
    reader.readAsText(file);
  }, [parseAndPreview]);

  // Bulk import
  const handleImport = useCallback(async () => {
    if (!parsed) return;
    const valid = parsed.filter(r => r._valid);
    if (valid.length === 0) return;

    setImporting(true);
    setImportResult(null);

    try {
      const attrs = valid.map(({ _rowNum, _errors, _valid, ...attr }) => attr);
      const token = getToken() || localStorage.getItem('erp_token') || '';
            const res = await fetch(`${API_URL}/api/attribute-engine-legacy/attributes/bulk`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ attributes: attrs }),
      });
      const json = await res.json();
      if (json.success) {
        setImportResult(json);
        onImportComplete?.();
      } else {
        alert(`Import failed: ${json.error}`);
      }
    } catch (err) {
      alert(`Import error: ${err.message}`);
    } finally {
      setImporting(false);
    }
  }, [parsed, onImportComplete]);

  const validCount = parsed ? parsed.filter(r => r._valid).length : 0;
  const invalidCount = parsed ? parsed.filter(r => !r._valid).length : 0;

  return (
    <div style={{ maxWidth:'960px', margin:'0 auto', padding:'1.25rem 1.5rem' }}>
      {/* Header */}
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:'1.25rem', paddingBottom:'0.75rem', borderBottom:`1px solid ${borderColor}` }}>
        <div>
          <div style={{ fontSize:'0.68rem', fontWeight:700, color:textMuted, textTransform:'uppercase', letterSpacing:'0.04em', marginBottom:'0.2rem' }}>Bulk Operations</div>
          <h2 style={{ fontSize:'1rem', fontWeight:800, color:textPrimary, margin:0 }}>Bulk Import Attributes</h2>
          <p style={{ fontSize:'0.8rem', color:textMuted, marginTop:'0.25rem', margin:0 }}>
            Upload a CSV file or paste CSV data to import multiple attributes at once.
          </p>
        </div>
        <button onClick={downloadTemplate}
          style={{ display:'flex', alignItems:'center', gap:'6px', padding:'0.5rem 1rem', background:'#059669', color:'white', border:'none', borderRadius:'7px', cursor:'pointer', fontSize:'0.78rem', fontWeight:700, flexShrink:0 }}>
          <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
          Download Template CSV
        </button>
      </div>

      {/* Template Info Card */}
      <div style={{ background:'#eff6ff', borderRadius:'8px', border:'1px solid #bfdbfe', padding:'0.85rem 1rem', marginBottom:'1.25rem', display:'flex', gap:'0.75rem', alignItems:'flex-start' }}>
        <span style={{ fontSize:'1.1rem', flexShrink:0 }}>📋</span>
        <div>
          <div style={{ fontWeight:700, fontSize:'0.8rem', color:'#1e40af', marginBottom:'0.25rem' }}>CSV Format Guide</div>
          <div style={{ fontSize:'0.75rem', color:'#1e3a8a', lineHeight:1.6 }}>
            <strong>Required columns:</strong> <code style={{ background:'#dbeafe', padding:'1px 5px', borderRadius:'3px' }}>id, label, type</code>
            &nbsp;&nbsp;<strong>Options format:</strong> <code style={{ background:'#dbeafe', padding:'1px 5px', borderRadius:'3px' }}>value:Label|value2:Label2</code>
            &nbsp;&nbsp;<strong>Valid types:</strong> text, number, dropdown, multi-select, boolean, date, lookup, grid, status, image-url, address…
          </div>
        </div>
      </div>

      {/* Input Method Tabs */}
      <div style={{ display:'flex', gap:'0', background:'#f1f5f9', borderRadius:'8px', border:`1px solid ${borderColor}`, padding:'3px', marginBottom:'1rem', width:'fit-content' }}>
        {[{ id:'upload', label:'Upload File', icon:'📁' }, { id:'paste', label:'Paste CSV', icon:'📋' }].map(m => (
          <button key={m.id} onClick={() => setActiveInput(m.id)}
            style={{ padding:'0.42rem 1rem', borderRadius:'6px', border:'none', cursor:'pointer', fontSize:'0.75rem', fontWeight:700, display:'flex', alignItems:'center', gap:'5px', background: activeInput === m.id ? 'white' : 'transparent', color: activeInput === m.id ? textPrimary : textMuted, boxShadow: activeInput === m.id ? '0 1px 3px rgba(0,0,0,0.1)' : 'none', transition:'all 0.15s' }}>
            <span>{m.icon}</span>{m.label}
          </button>
        ))}
      </div>

      {/* Upload Zone */}
      {activeInput === 'upload' && (
        <div
          onDragOver={e => { e.preventDefault(); setIsDragging(true); }}
          onDragLeave={() => setIsDragging(false)}
          onDrop={e => { e.preventDefault(); setIsDragging(false); handleFile(e.dataTransfer.files[0]); }}
          onClick={() => fileRef.current?.click()}
          style={{ border:`2px dashed ${isDragging ? accent : '#cbd5e1'}`, borderRadius:'12px', padding:'3rem 2rem', textAlign:'center', cursor:'pointer', background: isDragging ? '#eff6ff' : '#f8fafc', transition:'all 0.2s', marginBottom:'1rem' }}>
          <input ref={fileRef} type="file" accept=".csv,.txt" style={{ display:'none' }} onChange={e => handleFile(e.target.files?.[0])} />
          <div style={{ fontSize:'2.5rem', marginBottom:'0.75rem' }}>{isDragging ? '📥' : '📂'}</div>
          <div style={{ fontWeight:700, fontSize:'0.9rem', color:textPrimary, marginBottom:'0.35rem' }}>
            {isDragging ? 'Drop your CSV file here' : 'Click to upload or drag & drop'}
          </div>
          <div style={{ fontSize:'0.75rem', color:textMuted }}>Supports .csv files • No size limit</div>
          {csvText && (
            <div style={{ marginTop:'0.75rem', padding:'0.35rem 0.85rem', background:'#dcfce7', color:'#15803d', borderRadius:'999px', display:'inline-block', fontSize:'0.72rem', fontWeight:700 }}>
              File loaded — {csvText.split('\n').length - 1} data rows
            </div>
          )}
        </div>
      )}

      {/* Paste Zone */}
      {activeInput === 'paste' && (
        <div style={{ marginBottom:'1rem' }}>
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'0.4rem' }}>
            <label style={{ fontSize:'0.75rem', fontWeight:700, color:textMuted, textTransform:'uppercase', letterSpacing:'0.04em' }}>Paste CSV Data</label>
            {csvText && (
              <button onClick={() => { setCsvText(''); setParsed(null); setImportResult(null); }}
                style={{ fontSize:'0.72rem', color:'#ef4444', background:'none', border:'none', cursor:'pointer', fontWeight:600 }}>Clear</button>
            )}
          </div>
          <textarea
            value={csvText}
            onChange={e => parseAndPreview(e.target.value)}
            placeholder={`id,label,type,industry,scope,options\nproduct_color,Product Color,multi-select,Common,form,red:Red|blue:Blue|green:Green\nproduct_weight,Product Weight,number,Common,form,`}
            style={{ ...inp, height:'160px', resize:'vertical', fontFamily:'monospace', fontSize:'0.75rem', lineHeight:1.5 }}
          />
        </div>
      )}

      {/* Parse Button (for upload mode) */}
      {activeInput === 'upload' && csvText && !parsed && (
        <button onClick={() => parseAndPreview(csvText)}
          style={{ padding:'0.5rem 1.25rem', background: accent, color:'white', border:'none', borderRadius:'7px', cursor:'pointer', fontSize:'0.8rem', fontWeight:700, marginBottom:'1rem' }}>
          Parse &amp; Preview
        </button>
      )}

      {/* Preview Table */}
      {parsed && parsed.length > 0 && (
        <div style={{ background:'white', borderRadius:'10px', border:`1px solid ${borderColor}`, overflow:'hidden', marginBottom:'1rem' }}>
          {/* Summary bar */}
          <div style={{ padding:'0.75rem 1rem', borderBottom:`1px solid ${borderColor}`, background:'#f8fafc', display:'flex', alignItems:'center', justifyContent:'space-between', flexWrap:'wrap', gap:'0.5rem' }}>
            <div style={{ display:'flex', alignItems:'center', gap:'1rem' }}>
              <span style={{ fontSize:'0.8rem', fontWeight:700, color:textPrimary }}>Preview — {parsed.length} row{parsed.length !== 1 ? 's' : ''}</span>
              <span style={{ padding:'2px 10px', background:'#dcfce7', color:'#15803d', borderRadius:'999px', fontSize:'0.7rem', fontWeight:700 }}>{validCount} Valid</span>
              {invalidCount > 0 && <span style={{ padding:'2px 10px', background:'#fee2e2', color:'#dc2626', borderRadius:'999px', fontSize:'0.7rem', fontWeight:700 }}>{invalidCount} Invalid</span>}
            </div>
            {importResult ? (
              <div style={{ padding:'0.35rem 0.85rem', background:'#dcfce7', color:'#15803d', borderRadius:'7px', fontSize:'0.78rem', fontWeight:700, display:'flex', alignItems:'center', gap:'6px' }}>
                ✓ Imported {importResult.imported} attributes{importResult.failed > 0 ? `, ${importResult.failed} failed` : ''}
              </div>
            ) : (
              <button onClick={handleImport} disabled={importing || validCount === 0}
                style={{ display:'flex', alignItems:'center', gap:'6px', padding:'0.45rem 1.1rem', background: validCount === 0 ? '#94a3b8' : accent, color:'white', border:'none', borderRadius:'7px', cursor: validCount === 0 ? 'default' : 'pointer', fontSize:'0.78rem', fontWeight:700 }}>
                {importing ? <><Spinner size={14} color="white" /> Importing...</> : `Import ${validCount} Valid Row${validCount !== 1 ? 's' : ''}`}
              </button>
            )}
          </div>

          {/* Table */}
          <div style={{ overflowX:'auto', maxHeight:'400px', overflowY:'auto' }}>
            <table style={{ width:'100%', borderCollapse:'collapse', fontSize:'0.75rem' }}>
              <thead>
                <tr style={{ background:'#f8fafc', borderBottom:`1px solid ${borderColor}`, position:'sticky', top:0 }}>
                  {['#', 'Status', 'ID', 'Label', 'Type', 'Industry', 'Scope', 'Options', 'Required', 'Issues'].map(h => (
                    <th key={h} style={{ padding:'0.5rem 0.75rem', textAlign:'left', fontWeight:700, color:textMuted, textTransform:'uppercase', fontSize:'0.65rem', letterSpacing:'0.04em', whiteSpace:'nowrap' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {parsed.map((row, idx) => {
                  // Find import result for this row
                  const result = importResult?.results?.find(r => r.id === row.id);
                  const rowBg = result?.status === 'imported' ? '#f0fdf4' : result?.status === 'error' ? '#fff1f2' : !row._valid ? '#fff7f7' : 'transparent';
                  return (
                    <tr key={idx} style={{ borderBottom:`1px solid #f8fafc`, background: rowBg }}>
                      <td style={{ padding:'0.45rem 0.75rem', color:'#94a3b8', fontWeight:600 }}>{row._rowNum}</td>
                      <td style={{ padding:'0.45rem 0.75rem' }}>
                        {result ? (
                          <span style={{ padding:'2px 8px', borderRadius:'999px', fontSize:'0.65rem', fontWeight:700, background: result.status === 'imported' ? '#dcfce7' : '#fee2e2', color: result.status === 'imported' ? '#15803d' : '#dc2626' }}>
                            {result.status === 'imported' ? '✓ Imported' : '✕ Failed'}
                          </span>
                        ) : (
                          <span style={{ padding:'2px 8px', borderRadius:'999px', fontSize:'0.65rem', fontWeight:700, background: row._valid ? '#dcfce7' : '#fee2e2', color: row._valid ? '#15803d' : '#dc2626' }}>
                            {row._valid ? '✓ Valid' : '✕ Invalid'}
                          </span>
                        )}
                      </td>
                      <td style={{ padding:'0.45rem 0.75rem', fontFamily:'monospace', color:'#334155', maxWidth:'150px', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{row.id}</td>
                      <td style={{ padding:'0.45rem 0.75rem', fontWeight:600, color:textPrimary, maxWidth:'160px', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{row.label}</td>
                      <td style={{ padding:'0.45rem 0.75rem' }}>
                        <span style={{ padding:'2px 7px', borderRadius:'3px', fontSize:'0.65rem', fontWeight:800, background:'#475569', color:'white', textTransform:'uppercase' }}>
                          {row.type}
                        </span>
                      </td>
                      <td style={{ padding:'0.45rem 0.75rem', color:textMuted, maxWidth:'100px', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{row.industry}</td>
                      <td style={{ padding:'0.45rem 0.75rem', color:textMuted }}>{row.scope}</td>
                      <td style={{ padding:'0.45rem 0.75rem', color:textMuted, maxWidth:'140px', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                        {row.options?.length ? `${row.options.length} opts` : '—'}
                      </td>
                      <td style={{ padding:'0.45rem 0.75rem', color:textMuted }}>
                        {row.validation?.required ? <span style={{ color:'#dc2626', fontWeight:700 }}>Yes</span> : 'No'}
                      </td>
                      <td style={{ padding:'0.45rem 0.75rem', color:'#dc2626', fontSize:'0.7rem', maxWidth:'200px' }}>
                        {result?.error || row._errors?.join('; ') || '—'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Empty parsed state */}
      {csvText && parsed && parsed.length === 0 && (
        <div style={{ padding:'2rem', textAlign:'center', background:'white', borderRadius:'10px', border:`2px dashed ${borderColor}` }}>
          <div style={{ fontSize:'2rem', marginBottom:'0.5rem' }}>⚠️</div>
          <div style={{ fontWeight:700, color:textPrimary }}>No data rows found</div>
          <div style={{ color:textMuted, fontSize:'0.8rem', marginTop:'0.25rem' }}>Make sure your CSV has a header row and at least one data row.</div>
        </div>
      )}

      {/* Tips */}
      {!csvText && (
        <div style={{ background:'white', borderRadius:'10px', border:`1px solid ${borderColor}`, padding:'1rem 1.25rem' }}>
          <div style={{ fontSize:'0.75rem', fontWeight:700, color:textMuted, textTransform:'uppercase', letterSpacing:'0.05em', marginBottom:'0.75rem' }}>
            Quick Start Guide
          </div>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'0.75rem' }}>
            {[
              { step:'1', title:'Download Template', desc:'Click "Download Template CSV" above to get the standard format with sample rows.' },
              { step:'2', title:'Fill in your data', desc:'Open in Excel or Google Sheets. Add your attributes row by row.' },
              { step:'3', title:'Upload or Paste', desc:'Upload the .csv file or paste the CSV text directly into the text area.' },
              { step:'4', title:'Review & Import', desc:'Check the preview table for validation errors, then click "Import Valid Rows".' },
            ].map(tip => (
              <div key={tip.step} style={{ display:'flex', gap:'0.75rem', alignItems:'flex-start' }}>
                <span style={{ width:24, height:24, borderRadius:'50%', background: accent, color:'white', display:'flex', alignItems:'center', justifyContent:'center', fontSize:'0.7rem', fontWeight:800, flexShrink:0 }}>{tip.step}</span>
                <div>
                  <div style={{ fontWeight:700, fontSize:'0.8rem', color:textPrimary, marginBottom:'0.15rem' }}>{tip.title}</div>
                  <div style={{ fontSize:'0.75rem', color:textMuted, lineHeight:1.5 }}>{tip.desc}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
